import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { MeshBVHHelper } from 'three-mesh-bvh';
import { button, folder, useControls } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { GlbSceneApi } from '../../scene/glb/createGlbScene';
import type { GlbSceneConfig } from '../../scene/glb/glbSceneConfig';

interface GlbDevPanelProps {
  api: GlbSceneApi;
  config: GlbSceneConfig;
}

interface DevMarker {
  id: string;
  name: string;
  mesh: THREE.Mesh;
}

type ChangeContext = { initial?: boolean };

const MARKER_COLOR = 0xff6600;
const MARKER_RADIUS = 3;
const DEBUG_RENDER_ORDER = 10;

const markerGeometry = new THREE.SphereGeometry(MARKER_RADIUS, 16, 16);
const markerMaterial = new THREE.MeshBasicMaterial({
  color: MARKER_COLOR,
  transparent: true,
  opacity: 0.9,
  depthWrite: false,
  depthTest: false,
});

const colorToHex = (color: THREE.Color): string =>
  `#${color.getHexString()}`;

const roundVec3 = (v: THREE.Vector3) => ({
  x: +v.x.toFixed(4),
  y: +v.y.toFixed(4),
  z: +v.z.toFixed(4),
});

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text).then(
    () => console.log(`[GlbDevPanel] ${label} copied to clipboard`),
    (err) => console.error(`[GlbDevPanel] Failed to copy ${label}`, err),
  );
};

const buildConfigJson = (api: GlbSceneApi, config: GlbSceneConfig): string => {
  const hemi = api.hemisphereLight;
  const result: Record<string, unknown> = {
    id: config.id,
    renderer: {
      clearColor: api.renderer.getClearColor(new THREE.Color()).getHex(),
      maxPixelRatio: config.renderer.maxPixelRatio,
      toneMapping: api.renderer.toneMapping,
      toneMappingExposure: +api.renderer.toneMappingExposure.toFixed(3),
    },
    camera: {
      fov: api.camera.fov,
      near: api.camera.near,
      far: api.camera.far,
      initialPosition: roundVec3(api.camera.position),
      initialTarget: roundVec3(api.controls.target),
    },
    controls: {
      target: roundVec3(api.controls.target),
      minDistance: api.controls.minDistance,
      maxDistance: api.controls.maxDistance,
      minPolarAngle: +api.controls.minPolarAngle.toFixed(4),
      maxPolarAngle: +api.controls.maxPolarAngle.toFixed(4),
      minAzimuthAngle: +api.controls.minAzimuthAngle.toFixed(4),
      maxAzimuthAngle: +api.controls.maxAzimuthAngle.toFixed(4),
      enablePan: api.controls.enablePan,
      enableDamping: api.controls.enableDamping,
      dampingFactor: api.controls.dampingFactor,
      autoRotate: api.controls.autoRotate,
      autoRotateSpeed: api.controls.autoRotateSpeed,
    },
    models: config.models.map((entry) => {
      const root = api.modelRoots.get(entry.key);
      if (!root) return entry;
      return {
        ...entry,
        transform: {
          position: roundVec3(root.position),
          rotation: {
            x: +root.rotation.x.toFixed(4),
            y: +root.rotation.y.toFixed(4),
            z: +root.rotation.z.toFixed(4),
          },
          scale: roundVec3(root.scale),
        },
      };
    }),
    environment: {
      hemisphereLight: {
        skyColor: hemi.color.getHex(),
        groundColor: hemi.groundColor.getHex(),
        intensity: +hemi.intensity.toFixed(3),
      },
      directionalLights: api.directionalLights.map((dl) => ({
        color: dl.color.getHex(),
        intensity: +dl.intensity.toFixed(3),
        position: {
          x: +dl.position.x.toFixed(2),
          y: +dl.position.y.toFixed(2),
          z: +dl.position.z.toFixed(2),
        },
      })),
      backgroundColor: (api.scene.background as THREE.Color)?.getHex?.() ?? config.environment.backgroundColor,
      envMapIntensity: +api.scene.environmentIntensity.toFixed(3),
    },
  };

  if (api.groundPlane) {
    const mesh = api.groundPlane;
    const mat = mesh.material as THREE.MeshStandardMaterial;
    const geo = mesh.geometry as THREE.PlaneGeometry;
    result.groundPlane = {
      size: geo.parameters.width,
      color: mat.color.getHex(),
      opacity: +mat.opacity.toFixed(2),
      positionY: +mesh.position.y.toFixed(2),
      ...(mat.map ? { textureRepeat: mat.map.repeat.x } : {}),
      ...(config.groundPlane?.textureUrl ? { textureUrl: config.groundPlane.textureUrl } : {}),
    };
  }

  return JSON.stringify(result, null, 2);
};

export const GlbDevPanel = ({ api, config }: GlbDevPanelProps) => {
  const gridRef = useRef<THREE.GridHelper | null>(null);
  const axesRef = useRef<THREE.AxesHelper | null>(null);

  // --- Marker state ---
  const markersRef = useRef<DevMarker[]>([]);
  const markerCounterRef = useRef(0);
  const [markerVersion, setMarkerVersion] = useState(0);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const selectedMarkerIdRef = useRef<string | null>(null);
  const transformControlsRef = useRef<TransformControls | null>(null);
  const isProgrammaticRef = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerSetRef = useRef<((values: Record<string, any>) => void) | null>(null);

  // --- Light helpers state ---
  const lightHelpersRef = useRef<THREE.DirectionalLightHelper[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dlSetRef = useRef<((values: Record<string, any>) => void) | null>(null);
  const [selectedLightIdx, setSelectedLightIdx] = useState<number | null>(null);
  const selectedLightIdxRef = useRef<number | null>(null);

  // --- Renderer ---
  const TONE_MAPPING_OPTIONS: Record<string, number> = {
    None: THREE.NoToneMapping,
    Linear: THREE.LinearToneMapping,
    Reinhard: THREE.ReinhardToneMapping,
    Cineon: THREE.CineonToneMapping,
    'ACES Filmic': THREE.ACESFilmicToneMapping,
    AgX: THREE.AgXToneMapping,
    Neutral: THREE.NeutralToneMapping,
  };

  const initialToneMappingLabel =
    Object.entries(TONE_MAPPING_OPTIONS).find(([, v]) => v === api.renderer.toneMapping)?.[0] ?? 'None';

  useControls('Renderer', {
    toneMapping: {
      label: 'Tone Mapping',
      value: initialToneMappingLabel,
      options: Object.keys(TONE_MAPPING_OPTIONS),
      onChange: (v: string) => {
        api.renderer.toneMapping = (TONE_MAPPING_OPTIONS[v] ?? THREE.NoToneMapping) as THREE.ToneMapping;
      },
    },
    exposure: {
      label: 'Exposure',
      value: api.renderer.toneMappingExposure,
      min: 0, max: 10, step: 0.01,
      onChange: (v: number) => { api.renderer.toneMappingExposure = v; },
    },
    'Copy##renderer': button(() => {
      copyToClipboard(JSON.stringify({
        clearColor: api.renderer.getClearColor(new THREE.Color()).getHex(),
        toneMapping: api.renderer.toneMapping,
        toneMappingExposure: +api.renderer.toneMappingExposure.toFixed(3),
      }, null, 2), 'Renderer');
    }),
  });

  // --- Hemisphere Light ---
  useControls('Hemisphere Light', {
    skyColor: {
      value: colorToHex(api.hemisphereLight.color),
      onChange: (v: string) => { api.hemisphereLight.color.set(v); },
    },
    groundColor: {
      value: colorToHex(api.hemisphereLight.groundColor),
      onChange: (v: string) => { api.hemisphereLight.groundColor.set(v); },
    },
    intensity: {
      value: api.hemisphereLight.intensity,
      min: 0, max: 3, step: 0.01,
      onChange: (v: number) => { api.hemisphereLight.intensity = v; },
    },
    'Copy##hemi': button(() => {
      copyToClipboard(JSON.stringify({
        skyColor: api.hemisphereLight.color.getHex(),
        groundColor: api.hemisphereLight.groundColor.getHex(),
        intensity: +api.hemisphereLight.intensity.toFixed(3),
      }, null, 2), 'Hemisphere Light');
    }),
  });

  // --- Directional Lights ---
  const selectLight = useCallback((idx: number | null) => {
    const gizmo = transformControlsRef.current;
    if (!gizmo) return;

    if (idx === null || idx === selectedLightIdxRef.current) {
      // Only detach if a light is currently selected (not a marker)
      if (selectedLightIdxRef.current !== null) {
        gizmo.detach();
        gizmo.getHelper().visible = false;
      }
      selectedLightIdxRef.current = null;
      setSelectedLightIdx(null);
      return;
    }

    const dl = api.directionalLights[idx];
    if (!dl) return;

    // Deselect any marker
    if (selectedMarkerIdRef.current !== null) {
      selectedMarkerIdRef.current = null;
      setSelectedMarkerId(null);
    }

    gizmo.attach(dl);
    gizmo.getHelper().visible = true;
    selectedLightIdxRef.current = idx;
    setSelectedLightIdx(idx);
  }, [api]);

  const dlFolders: Schema = {};

  dlFolders['Show Helpers'] = {
    value: false,
    onChange: (v: boolean) => {
      if (v && lightHelpersRef.current.length === 0) {
        for (const dl of api.directionalLights) {
          const helper = new THREE.DirectionalLightHelper(dl, 1);
          api.scene.add(helper);
          lightHelpersRef.current.push(helper);
        }
      } else if (!v && lightHelpersRef.current.length > 0) {
        for (const helper of lightHelpersRef.current) {
          api.scene.remove(helper);
          helper.dispose();
        }
        lightHelpersRef.current = [];
      }
    },
  };

  api.directionalLights.forEach((dl, i) => {
    const isSelected = selectedLightIdx === i;
    const lightFolder: Schema = {
      [isSelected ? `Deselect Gizmo##dl${i}` : `Select Gizmo##dl${i}`]: button(
        () => selectLight(isSelected ? null : i),
      ),
      [`color_${i}`]: {
        label: 'color',
        value: colorToHex(dl.color),
        onChange: (v: string) => { dl.color.set(v); },
      },
      [`intensity_${i}`]: {
        label: 'intensity',
        value: dl.intensity,
        min: 0, max: 5, step: 0.01,
        onChange: (v: number) => { dl.intensity = v; },
      },
      [`posX_${i}`]: {
        label: 'pos X',
        value: dl.position.x,
        min: -200, max: 200, step: 0.5,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          dl.position.x = v;
        },
      },
      [`posY_${i}`]: {
        label: 'pos Y',
        value: dl.position.y,
        min: -200, max: 200, step: 0.5,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          dl.position.y = v;
        },
      },
      [`posZ_${i}`]: {
        label: 'pos Z',
        value: dl.position.z,
        min: -200, max: 200, step: 0.5,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          dl.position.z = v;
        },
      },
    };
    const title = isSelected ? `● Light ${i}` : `Light ${i}`;
    dlFolders[title] = folder(lightFolder, { collapsed: false });
  });

  dlFolders['Copy##dl'] = button(() => {
    copyToClipboard(JSON.stringify(api.directionalLights.map((dl) => ({
      color: dl.color.getHex(),
      intensity: +dl.intensity.toFixed(3),
      position: { x: +dl.position.x.toFixed(2), y: +dl.position.y.toFixed(2), z: +dl.position.z.toFixed(2) },
    })), null, 2), 'Directional Lights');
  });
  const [, dlSet] = useControls('Directional Lights', () => dlFolders, [selectedLightIdx]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dlSetRef.current = dlSet as any;

  // --- Environment ---
  useControls('Environment', {
    intensity: {
      label: 'Env Map Intensity',
      value: api.scene.environmentIntensity,
      min: 0, max: 5, step: 0.01,
      onChange: (v: number) => { api.scene.environmentIntensity = v; },
    },
    'Copy##env': button(() => {
      copyToClipboard(JSON.stringify({
        envMapIntensity: +api.scene.environmentIntensity.toFixed(3),
      }, null, 2), 'Environment');
    }),
  });

  // --- Camera ---
  const [, setCameraControls] = useControls('Camera', () => ({
    fov: {
      value: api.camera.fov,
      min: 10, max: 120, step: 1,
      onChange: (v: number, _p: string, ctx: ChangeContext) => {
        if (ctx.initial) return;
        api.camera.fov = v;
        api.camera.updateProjectionMatrix();
      },
    },
  }));

  // Sync Leva UI with actual camera FOV (overrides stale localStorage value)
  useEffect(() => {
    setCameraControls({ fov: api.camera.fov });
  }, [api, setCameraControls]);

  // --- Camera Debug (live position & target + orbit-center marker) ---
  const orbitMarkerRef = useRef<THREE.Mesh | null>(null);
  const [, setCameraDebug] = useControls('Camera Debug', () => ({
    posX: { label: 'pos X', value: api.camera.position.x, step: 0.1, disabled: true },
    posY: { label: 'pos Y', value: api.camera.position.y, step: 0.1, disabled: true },
    posZ: { label: 'pos Z', value: api.camera.position.z, step: 0.1, disabled: true },
    tgtX: { label: 'target X', value: api.controls.target.x, step: 0.1, disabled: true },
    tgtY: { label: 'target Y', value: api.controls.target.y, step: 0.1, disabled: true },
    tgtZ: { label: 'target Z', value: api.controls.target.z, step: 0.1, disabled: true },
    polar: { label: 'polar', value: api.controls.getPolarAngle(), step: 0.01, disabled: true },
    azimuth: { label: 'azimuth', value: api.controls.getAzimuthalAngle(), step: 0.01, disabled: true },
    showOrbitCenter: {
      label: 'Show Orbit Center',
      value: false,
      onChange: (visible: boolean) => {
        if (visible && !orbitMarkerRef.current) {
          const geometry = new THREE.SphereGeometry(0.05, 16, 16);
          const material = new THREE.MeshBasicMaterial({
            color: 0x00ffff,
            transparent: true,
            opacity: 0.85,
            depthTest: false,
            depthWrite: false,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.renderOrder = DEBUG_RENDER_ORDER;
          mesh.position.copy(api.controls.target);
          api.scene.add(mesh);
          orbitMarkerRef.current = mesh;
        }
        if (orbitMarkerRef.current) {
          orbitMarkerRef.current.visible = visible;
        }
      },
    },
    'Copy Position': button(() => {
      const pos = roundVec3(api.camera.position);
      copyToClipboard(JSON.stringify(pos), 'Camera position');
    }),
    'Copy Target': button(() => {
      const tgt = roundVec3(api.controls.target);
      copyToClipboard(JSON.stringify(tgt), 'Camera target');
    }),
    'Copy Camera Config': button(() => {
      const cfg = {
        initialPosition: roundVec3(api.camera.position),
        initialTarget: roundVec3(api.controls.target),
      };
      copyToClipboard(JSON.stringify(cfg, null, 2), 'Camera config');
    }),
  }));

  useEffect(() => {
    let rafId: number;
    const prev = { px: 0, py: 0, pz: 0, tx: 0, ty: 0, tz: 0, pol: 0, azi: 0 };

    const tick = () => {
      const p = api.camera.position;
      const t = api.controls.target;
      const px = +p.x.toFixed(2);
      const py = +p.y.toFixed(2);
      const pz = +p.z.toFixed(2);
      const tx = +t.x.toFixed(2);
      const ty = +t.y.toFixed(2);
      const tz = +t.z.toFixed(2);
      const pol = +api.controls.getPolarAngle().toFixed(2);
      const azi = +api.controls.getAzimuthalAngle().toFixed(2);

      if (px !== prev.px || py !== prev.py || pz !== prev.pz ||
          tx !== prev.tx || ty !== prev.ty || tz !== prev.tz ||
          pol !== prev.pol || azi !== prev.azi) {
        setCameraDebug({
          posX: px, posY: py, posZ: pz,
          tgtX: tx, tgtY: ty, tgtZ: tz,
          polar: pol, azimuth: azi,
        });
        prev.px = px; prev.py = py; prev.pz = pz;
        prev.tx = tx; prev.ty = ty; prev.tz = tz;
        prev.pol = pol; prev.azi = azi;
      }
      const orbitMarker = orbitMarkerRef.current;
      if (orbitMarker?.visible) {
        orbitMarker.position.copy(api.controls.target);
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      const orbitMarker = orbitMarkerRef.current;
      if (orbitMarker) {
        api.scene.remove(orbitMarker);
        orbitMarker.geometry.dispose();
        (orbitMarker.material as THREE.Material).dispose();
        orbitMarkerRef.current = null;
      }
    };
  }, [api, setCameraDebug]);

  // --- Controls ---
  useControls('Controls', {
    'target X': {
      value: api.controls.target.x,
      min: -5000, max: 5000, step: 1,
      onChange: (v: number) => { api.controls.target.x = v; },
    },
    'target Y': {
      value: api.controls.target.y,
      min: -5000, max: 5000, step: 1,
      onChange: (v: number) => { api.controls.target.y = v; },
    },
    'target Z': {
      value: api.controls.target.z,
      min: -5000, max: 5000, step: 1,
      onChange: (v: number) => { api.controls.target.z = v; },
    },
    minDistance: {
      value: api.controls.minDistance,
      min: 0, max: 1000, step: 1,
      onChange: (v: number) => { api.controls.minDistance = v; },
    },
    maxDistance: {
      value: api.controls.maxDistance,
      min: 0, max: 5000, step: 1,
      onChange: (v: number) => { api.controls.maxDistance = v; },
    },
    minPolarAngle: {
      value: api.controls.minPolarAngle,
      min: 0, max: Math.PI, step: 0.01,
      onChange: (v: number) => { api.controls.minPolarAngle = v; },
    },
    maxPolarAngle: {
      value: api.controls.maxPolarAngle,
      min: 0, max: Math.PI, step: 0.01,
      onChange: (v: number) => { api.controls.maxPolarAngle = v; },
    },
    minAzimuthAngle: {
      value: api.controls.minAzimuthAngle,
      min: -Math.PI, max: Math.PI, step: 0.01,
      onChange: (v: number) => { api.controls.minAzimuthAngle = v; },
    },
    maxAzimuthAngle: {
      value: api.controls.maxAzimuthAngle,
      min: -Math.PI, max: Math.PI, step: 0.01,
      onChange: (v: number) => { api.controls.maxAzimuthAngle = v; },
    },
    enableDamping: {
      value: api.controls.enableDamping,
      onChange: (v: boolean) => { api.controls.enableDamping = v; },
    },
    dampingFactor: {
      value: api.controls.dampingFactor,
      min: 0, max: 1, step: 0.01,
      onChange: (v: number) => { api.controls.dampingFactor = v; },
    },
    enablePan: {
      label: 'Enable Pan (RMB)',
      value: api.controls.enablePan,
      onChange: (v: boolean) => { api.controls.enablePan = v; },
    },
    autoRotate: {
      value: api.controls.autoRotate,
      onChange: (v: boolean) => { api.controls.autoRotate = v; },
    },
    autoRotateSpeed: {
      value: api.controls.autoRotateSpeed,
      min: -10, max: 10, step: 0.1,
      onChange: (v: number) => { api.controls.autoRotateSpeed = v; },
    },
    'Copy##controls': button(() => {
      copyToClipboard(JSON.stringify({
        target: roundVec3(api.controls.target),
        minDistance: api.controls.minDistance,
        maxDistance: api.controls.maxDistance,
        minPolarAngle: +api.controls.minPolarAngle.toFixed(4),
        maxPolarAngle: +api.controls.maxPolarAngle.toFixed(4),
        minAzimuthAngle: +api.controls.minAzimuthAngle.toFixed(4),
        maxAzimuthAngle: +api.controls.maxAzimuthAngle.toFixed(4),
        enablePan: api.controls.enablePan,
        enableDamping: api.controls.enableDamping,
        dampingFactor: api.controls.dampingFactor,
        autoRotate: api.controls.autoRotate,
        autoRotateSpeed: api.controls.autoRotateSpeed,
      }, null, 2), 'Controls');
    }),
  });

  // --- Background ---
  useControls('Background', {
    color: {
      value: colorToHex(api.scene.background as THREE.Color),
      onChange: (v: string) => {
        const c = api.scene.background as THREE.Color;
        c.set(v);
        api.renderer.setClearColor(c);
      },
    },
    'Copy##bg': button(() => {
      copyToClipboard(JSON.stringify({
        backgroundColor: (api.scene.background as THREE.Color)?.getHex?.() ?? 0,
      }, null, 2), 'Background');
    }),
  });

  // --- Models ---
  const modelFolders: Schema = {};
  config.models.forEach((entry) => {
    const root = api.modelRoots.get(entry.key);
    if (!root) return;
    modelFolders[entry.key] = folder({
      [`${entry.key}_posX`]: {
        label: 'pos X', value: root.position.x, min: -5000, max: 5000, step: 1,
        onChange: (v: number) => { root.position.x = v; },
      },
      [`${entry.key}_posY`]: {
        label: 'pos Y', value: root.position.y, min: -5000, max: 5000, step: 1,
        onChange: (v: number) => { root.position.y = v; },
      },
      [`${entry.key}_posZ`]: {
        label: 'pos Z', value: root.position.z, min: -5000, max: 5000, step: 1,
        onChange: (v: number) => { root.position.z = v; },
      },
      [`${entry.key}_rotX`]: {
        label: 'rot X', value: root.rotation.x, min: -Math.PI, max: Math.PI, step: 0.01,
        onChange: (v: number) => { root.rotation.x = v; },
      },
      [`${entry.key}_rotY`]: {
        label: 'rot Y', value: root.rotation.y, min: -Math.PI, max: Math.PI, step: 0.01,
        onChange: (v: number) => { root.rotation.y = v; },
      },
      [`${entry.key}_rotZ`]: {
        label: 'rot Z', value: root.rotation.z, min: -Math.PI, max: Math.PI, step: 0.01,
        onChange: (v: number) => { root.rotation.z = v; },
      },
      [`${entry.key}_scaleX`]: {
        label: 'scale X', value: root.scale.x, min: 0.001, max: 100, step: 0.01,
        onChange: (v: number) => { root.scale.x = v; },
      },
      [`${entry.key}_scaleY`]: {
        label: 'scale Y', value: root.scale.y, min: 0.001, max: 100, step: 0.01,
        onChange: (v: number) => { root.scale.y = v; },
      },
      [`${entry.key}_scaleZ`]: {
        label: 'scale Z', value: root.scale.z, min: 0.001, max: 100, step: 0.01,
        onChange: (v: number) => { root.scale.z = v; },
      },
    }, { collapsed: true });
  });

  useControls('Models', modelFolders);

  // --- Ground Plane ---
  const gpSchema: Schema = {};
  if (api.groundPlane) {
    const mesh = api.groundPlane;
    const mat = mesh.material as THREE.MeshStandardMaterial;
    Object.assign(gpSchema, {
      visible: {
        value: mesh.visible,
        onChange: (v: boolean) => { mesh.visible = v; },
      },
      color: {
        value: colorToHex(mat.color),
        onChange: (v: string) => { mat.color.set(v); },
      },
      size: {
        value: (mesh.geometry as THREE.PlaneGeometry).parameters.width,
        min: 100, max: 50000, step: 100,
        onChange: (v: number) => {
          mesh.geometry.dispose();
          mesh.geometry = new THREE.PlaneGeometry(v, v);
        },
      },
      positionY: {
        value: mesh.position.y,
        min: -100, max: 100, step: 0.1,
        onChange: (v: number) => { mesh.position.y = v; },
      },
      opacity: {
        value: mat.opacity,
        min: 0, max: 1, step: 0.01,
        onChange: (v: number) => {
          mat.opacity = v;
          mat.transparent = v < 1;
          mat.needsUpdate = true;
        },
      },
      textureRepeat: {
        value: mat.map?.repeat.x ?? 1,
        min: 1, max: 100, step: 1,
        onChange: (v: number) => {
          if (mat.map) {
            mat.map.repeat.set(v, v);
          }
        },
      },
    });
  }
  useControls('Ground Plane', gpSchema);

  // --- Helpers ---
  useControls('Helpers', {
    grid: {
      value: false,
      onChange: (v: boolean) => {
        if (v && !gridRef.current) {
          gridRef.current = new THREE.GridHelper(500, 50);
          api.scene.add(gridRef.current);
        } else if (!v && gridRef.current) {
          api.scene.remove(gridRef.current);
          gridRef.current.dispose();
          gridRef.current = null;
        }
      },
    },
    axes: {
      value: false,
      onChange: (v: boolean) => {
        if (v && !axesRef.current) {
          axesRef.current = new THREE.AxesHelper(100);
          api.scene.add(axesRef.current);
        } else if (!v && axesRef.current) {
          api.scene.remove(axesRef.current);
          axesRef.current.dispose();
          axesRef.current = null;
        }
      },
    },
  });

  // --- Marker management functions ---
  const selectMarker = useCallback((id: string | null) => {
    const gizmo = transformControlsRef.current;
    if (!gizmo) return;

    if (id === null || id === selectedMarkerIdRef.current) {
      // Only detach if a marker is currently selected (not a light)
      if (selectedMarkerIdRef.current !== null) {
        gizmo.detach();
        gizmo.getHelper().visible = false;
      }
      selectedMarkerIdRef.current = null;
      setSelectedMarkerId(null);
      return;
    }

    const marker = markersRef.current.find((m) => m.id === id);
    if (!marker) return;

    // Deselect any light
    if (selectedLightIdxRef.current !== null) {
      selectedLightIdxRef.current = null;
      setSelectedLightIdx(null);
    }

    gizmo.attach(marker.mesh);
    gizmo.getHelper().visible = true;
    selectedMarkerIdRef.current = id;
    setSelectedMarkerId(id);
  }, []);

  const removeMarker = useCallback((id: string) => {
    const idx = markersRef.current.findIndex((m) => m.id === id);
    if (idx === -1) return;
    const marker = markersRef.current[idx];

    if (selectedMarkerIdRef.current === id) {
      selectMarker(null);
    }

    api.scene.remove(marker.mesh);
    marker.mesh.geometry.dispose();
    (marker.mesh.material as THREE.Material).dispose();
    markersRef.current.splice(idx, 1);
    setMarkerVersion((v) => v + 1);
  }, [api, selectMarker]);

  const addMarker = useCallback(() => {
    markerCounterRef.current += 1;
    const id = `marker-${markerCounterRef.current}`;
    const name = `Marker ${markerCounterRef.current}`;

    const mesh = new THREE.Mesh(markerGeometry.clone(), markerMaterial.clone());
    mesh.position.copy(api.controls.target);
    mesh.renderOrder = DEBUG_RENDER_ORDER;
    mesh.name = id;
    api.scene.add(mesh);

    markersRef.current.push({ id, name, mesh });
    setMarkerVersion((v) => v + 1);

    // Auto-select the new marker
    requestAnimationFrame(() => selectMarker(id));
  }, [api, selectMarker]);

  const removeAllMarkers = useCallback(() => {
    for (const marker of [...markersRef.current]) {
      if (selectedMarkerIdRef.current === marker.id) {
        selectMarker(null);
      }
      api.scene.remove(marker.mesh);
      marker.mesh.geometry.dispose();
      (marker.mesh.material as THREE.Material).dispose();
    }
    markersRef.current = [];
    setMarkerVersion((v) => v + 1);
  }, [api, selectMarker]);

  // --- Markers Leva panel ---
  const buildMarkerSchema = useCallback((): Schema => {
    const schema: Schema = {};
    const markers = markersRef.current;

    schema['Add Marker'] = button(() => addMarker());

    if (markers.length > 0) {
      schema['Copy All'] = button(() => {
        const payload = markers.map((m) => ({
          name: m.name,
          position: roundVec3(m.mesh.position),
        }));
        copyToClipboard(JSON.stringify(payload, null, 2), 'All markers');
      });

      schema['Remove All'] = button(() => removeAllMarkers());
    }

    for (const marker of markers) {
      const isSelected = selectedMarkerId === marker.id;
      const markerFolder: Schema = {};

      const selectLabel = isSelected ? 'Deselect Gizmo' : 'Select Gizmo';
      markerFolder[`${selectLabel}##${marker.id}`] = button(
        () => selectMarker(isSelected ? null : marker.id),
      );

      markerFolder[`${marker.id}_posX`] = {
        label: 'X',
        value: +marker.mesh.position.x.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.x = v;
        },
      };
      markerFolder[`${marker.id}_posY`] = {
        label: 'Y',
        value: +marker.mesh.position.y.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.y = v;
        },
      };
      markerFolder[`${marker.id}_posZ`] = {
        label: 'Z',
        value: +marker.mesh.position.z.toFixed(4),
        step: 0.1,
        onChange: (v: number, _p: string, ctx: ChangeContext) => {
          if (ctx.initial || isProgrammaticRef.current) return;
          marker.mesh.position.z = v;
        },
      };

      markerFolder[`Copy Position##${marker.id}`] = button(() => {
        const pos = roundVec3(marker.mesh.position);
        copyToClipboard(JSON.stringify(pos), `Marker "${marker.name}"`);
      });

      markerFolder[`Remove##${marker.id}`] = button(
        () => removeMarker(marker.id),
      );

      const title = isSelected ? `● ${marker.name}` : marker.name;
      schema[title] = folder(markerFolder, { collapsed: !isSelected });
    }

    return schema;
  }, [markerVersion, selectedMarkerId, addMarker, removeMarker, removeAllMarkers, selectMarker]);

  const [, markerSet] = useControls('Markers', buildMarkerSchema, [buildMarkerSchema]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  markerSetRef.current = markerSet as any;

  // --- TransformControls setup ---
  useEffect(() => {
    const gizmo = new TransformControls(api.camera, api.renderer.domElement);
    gizmo.setMode('translate');
    gizmo.setSize(0.7);

    const helper = gizmo.getHelper();
    helper.visible = false;
    api.scene.add(helper);

    gizmo.addEventListener('dragging-changed', (event: { value: unknown }) => {
      api.controls.enabled = !(event.value as boolean);
    });

    gizmo.addEventListener('objectChange', () => {
      const obj = gizmo.object;
      if (!obj) return;

      // Sync marker sliders
      const marker = markersRef.current.find((m) => m.mesh === obj);
      if (marker) {
        const setFn = markerSetRef.current;
        if (setFn) {
          isProgrammaticRef.current = true;
          setFn({
            [`${marker.id}_posX`]: +obj.position.x.toFixed(4),
            [`${marker.id}_posY`]: +obj.position.y.toFixed(4),
            [`${marker.id}_posZ`]: +obj.position.z.toFixed(4),
          });
          isProgrammaticRef.current = false;
        }
        return;
      }

      // Sync directional light sliders
      const lightIdx = api.directionalLights.indexOf(obj as THREE.DirectionalLight);
      if (lightIdx !== -1) {
        const setFn = dlSetRef.current;
        if (setFn) {
          isProgrammaticRef.current = true;
          setFn({
            [`posX_${lightIdx}`]: +obj.position.x.toFixed(2),
            [`posY_${lightIdx}`]: +obj.position.y.toFixed(2),
            [`posZ_${lightIdx}`]: +obj.position.z.toFixed(2),
          });
          isProgrammaticRef.current = false;
        }
        // Update light helper if visible
        const helper = lightHelpersRef.current[lightIdx];
        if (helper) helper.update();
      }
    });

    transformControlsRef.current = gizmo;

    return () => {
      gizmo.detach();
      api.scene.remove(helper);
      gizmo.dispose();
      transformControlsRef.current = null;
    };
  }, [api]);

  // --- Collision / BVH ---
  const bvhHelperRef = useRef<MeshBVHHelper | null>(null);
  const bvhDepthRef = useRef(10);

  const collisionSchema: Schema = {};
  if (api.collisionMesh) {
    collisionSchema['Show BVH'] = {
      value: false,
      onChange: (v: boolean) => {
        if (v && api.collisionMesh && !bvhHelperRef.current) {
          const helper = new MeshBVHHelper(api.collisionMesh, bvhDepthRef.current);
          helper.update();
          helper.visible = true;
          api.scene.add(helper);
          bvhHelperRef.current = helper;
        } else if (!v && bvhHelperRef.current) {
          api.scene.remove(bvhHelperRef.current);
          bvhHelperRef.current.traverse((child) => {
            if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
              child.geometry?.dispose();
            }
          });
          bvhHelperRef.current = null;
        }
      },
    };
    collisionSchema['BVH Depth'] = {
      value: 10,
      min: 1, max: 20, step: 1,
      onChange: (v: number) => {
        bvhDepthRef.current = v;
        if (bvhHelperRef.current) {
          bvhHelperRef.current.depth = v;
          bvhHelperRef.current.update();
        }
      },
    };
  }
  if (api.collisionSystem) {
    collisionSchema['Enabled'] = {
      value: api.collisionSystem.getConfig().enabled,
      onChange: (v: boolean) => {
        api.collisionSystem?.updateConfig({ enabled: v });
      },
    };
    collisionSchema['Camera Radius'] = {
      value: api.collisionSystem.getConfig().cameraRadius,
      min: 0.01, max: 5, step: 0.01,
      onChange: (v: number) => {
        api.collisionSystem?.updateConfig({ cameraRadius: v });
      },
    };
  }
  useControls('Collision', collisionSchema);

  // --- Mesh Inspector ---
  const [inspectorEnabled, setInspectorEnabled] = useState(false);
  const displacedMeshesRef = useRef<Map<THREE.Object3D, THREE.Vector3>>(new Map());

  const resetDisplaced = useCallback(() => {
    for (const [mesh, savedPos] of displacedMeshesRef.current) {
      mesh.position.copy(savedPos);
    }
    displacedMeshesRef.current.clear();
  }, []);

  useEffect(() => {
    if (!inspectorEnabled) {
      resetDisplaced();
      return;
    }

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const handleClick = (event: MouseEvent) => {
      const rect = api.renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, api.camera);

      const modelRoots = [...api.modelRoots.values()];
      const intersects = raycaster.intersectObjects(modelRoots, true);
      if (intersects.length === 0) return;

      const mesh = intersects[0].object;

      console.log('[Mesh Inspector]', {
        name: mesh.name,
        type: mesh.type,
        parent: mesh.parent?.name,
        position: mesh.position.toArray().map((v) => +v.toFixed(4)),
        worldPosition: mesh.getWorldPosition(new THREE.Vector3()).toArray().map((v) => +v.toFixed(4)),
      });

      if (displacedMeshesRef.current.has(mesh)) {
        mesh.position.copy(displacedMeshesRef.current.get(mesh)!);
        displacedMeshesRef.current.delete(mesh);
      } else {
        displacedMeshesRef.current.set(mesh, mesh.position.clone());
        const distance = mesh.position.distanceTo(api.controls.target);
        const offset = Math.max(distance * 0.4, 2);
        mesh.position.x += offset;
      }
    };

    api.renderer.domElement.addEventListener('click', handleClick);
    return () => {
      api.renderer.domElement.removeEventListener('click', handleClick);
      resetDisplaced();
    };
  }, [api, inspectorEnabled, resetDisplaced]);

  useControls('Mesh Inspector', {
    'Dump Scene Graph': button(() => {
      for (const [key, root] of api.modelRoots) {
        console.group(`[GLB] Scene graph for "${key}"`);
        let meshCount = 0;
        root.traverse((obj: THREE.Object3D) => {
          const depth = [];
          let parent = obj.parent;
          while (parent && parent !== root.parent) {
            depth.push('  ');
            parent = parent.parent;
          }
          const isMesh = (obj as THREE.Mesh).isMesh;
          if (isMesh) meshCount++;
          const type = isMesh ? 'Mesh' : obj.type;
          console.log(`${depth.join('')}${type}: "${obj.name}"`);
        });
        console.log(`Total meshes: ${meshCount}`);
        console.groupEnd();
      }
    }),
    'Click Inspector': {
      value: false,
      onChange: (v: boolean) => setInspectorEnabled(v),
    },
    'Reset Displaced': button(() => resetDisplaced()),
  });

  // --- Actions ---
  useControls('Actions', {
    'Copy Config': button(() => {
      const json = buildConfigJson(api, config);
      copyToClipboard(json, 'Config');
    }),
    'Clear Storage & Reload': button(() => {
      localStorage.clear();
      window.location.reload();
    }),
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gridRef.current) {
        api.scene.remove(gridRef.current);
        gridRef.current.dispose();
        gridRef.current = null;
      }
      if (axesRef.current) {
        api.scene.remove(axesRef.current);
        axesRef.current.dispose();
        axesRef.current = null;
      }
      for (const marker of markersRef.current) {
        api.scene.remove(marker.mesh);
        marker.mesh.geometry.dispose();
        (marker.mesh.material as THREE.Material).dispose();
      }
      markersRef.current = [];
      resetDisplaced();
      for (const helper of lightHelpersRef.current) {
        api.scene.remove(helper);
        helper.dispose();
      }
      lightHelpersRef.current = [];
      if (bvhHelperRef.current) {
        api.scene.remove(bvhHelperRef.current);
        bvhHelperRef.current.traverse((child) => {
            if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
              child.geometry?.dispose();
            }
          });
        bvhHelperRef.current = null;
      }
    };
  }, [api, resetDisplaced]);

  return null;
};

GlbDevPanel.displayName = 'GlbDevPanel';
